const DEFAULT_THEME = {
  primary: '#2563eb',
  secondary: '#64748b',
}

export function normalizeHex(color) {
  if (color == null || typeof color !== 'string') return null
  const s = color.trim()
  if (!/^#?[0-9a-fA-F]{3,8}$/.test(s)) return null
  let hex = s.startsWith('#') ? s.slice(1) : s
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('')
  }
  if (hex.length !== 6) return null
  return `#${hex.toLowerCase()}`
}

function hexToRgb(hex) {
  const normalized = normalizeHex(hex)
  if (!normalized) return null
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  }
}

function rgbToHex(r, g, b) {
  const channel = (c) =>
    Math.max(0, Math.min(255, Math.round(c)))
      .toString(16)
      .padStart(2, '0')
  return `#${channel(r)}${channel(g)}${channel(b)}`
}

export function mixHex(hexA, hexB, ratio = 0.5) {
  const a = hexToRgb(hexA)
  const b = hexToRgb(hexB)
  if (!a || !b) return hexA
  const t = Math.max(0, Math.min(1, ratio))
  return rgbToHex(
    a.r + (b.r - a.r) * t,
    a.g + (b.g - a.g) * t,
    a.b + (b.b - a.b) * t,
  )
}

export function darkenHex(hex, amount = 0.15) {
  return mixHex(hex, '#000000', amount)
}

export function lightenHex(hex, amount = 0.15) {
  return mixHex(hex, '#ffffff', amount)
}

/** Colores de marca desde GET .../info (`data.primaryColor` o `data.data.primaryColor`). */
export function buildTenantTheme(infoData) {
  const nested = infoData?.data
  const primary =
    normalizeHex(nested?.primaryColor ?? infoData?.primaryColor) ?? DEFAULT_THEME.primary
  const secondary =
    normalizeHex(nested?.secondaryColor ?? infoData?.secondaryColor) ?? DEFAULT_THEME.secondary
  return {
    primary,
    secondary,
    primaryHover: darkenHex(primary, 0.15),
  }
}

export function applyTenantTheme(theme) {
  const t = theme ?? DEFAULT_THEME
  const primary = t.primary ?? DEFAULT_THEME.primary
  const secondary = t.secondary ?? DEFAULT_THEME.secondary
  const primaryHover = t.primaryHover ?? darkenHex(primary, 0.15)

  const root = document.documentElement
  root.style.setProperty('--tenant-primary', primary)
  root.style.setProperty('--tenant-primary-hover', primaryHover)
  root.style.setProperty('--tenant-secondary', secondary)

  root.style.setProperty('--tenant-dark', mixHex(primary, '#0c0c0c', 0.78))
  root.style.setProperty('--tenant-dark-soft', mixHex(primary, '#1a1a1a', 0.55))
  root.style.setProperty('--tenant-page-bg', mixHex(secondary, '#f4f5f7', 0.88))
  root.style.setProperty('--tenant-surface', mixHex(secondary, '#ffffff', 0.94))
  root.style.setProperty('--tenant-text', mixHex(primary, '#111827', 0.82))
  root.style.setProperty('--tenant-text-muted', mixHex(secondary, '#64748b', 0.45))
  root.style.setProperty('--tenant-border', mixHex(secondary, '#e2e8f0', 0.35))
  root.style.setProperty('--tenant-primary-soft', mixHex(primary, '#ffffff', 0.9))
  root.style.setProperty('--tenant-secondary-soft', mixHex(secondary, '#ffffff', 0.82))
  root.style.setProperty('--tenant-on-primary', '#ffffff')
}

export { DEFAULT_THEME }
